import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { resolveFileUrl } from '../../lib/portalHelpers';
import type { Certificate, PortalClass, StudentProfile } from '../../types/portal';
import { Plus, Trash2, X, Check, Upload, FileText, Loader2, Award, ExternalLink, Search } from 'lucide-react';

const cardClass =
  'bg-white rounded-[24px] border border-[#eadfd6] shadow-[0_10px_35px_rgba(0,0,0,0.05)]';
const inputClass =
  'w-full px-4 py-3 border border-[#ddd6cf] rounded-2xl focus:ring-2 focus:ring-[#970000]/10 focus:border-[#970000] outline-none transition-colors duration-200 bg-white';
const primaryBtn =
  'inline-flex items-center px-4 py-2 rounded-full bg-[#970000] text-white hover:bg-[#7a0000] transition-colors duration-200 font-medium text-sm';
const secondaryBtn =
  'inline-flex items-center px-4 py-2 rounded-full border border-[#d9c9bd] text-[#5f5148] hover:bg-[#f8f3ef] transition-colors duration-200 font-medium text-sm';
const dangerBtn =
  'inline-flex items-center px-4 py-2 rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors duration-200 font-medium text-sm';

const todayIso = () => new Date().toISOString().substring(0, 10);

const emptyForm = () => ({
  student_id: '',
  class_id: '',
  title: 'Teilnahmezertifikat',
  issued_at: todayIso(),
});

const studentName = (s: StudentProfile) =>
  `${s.first_name} ${s.last_name}`.trim() || s.display_name || 'Unbenannt';

const CertificatesManager: React.FC = () => {
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [students, setStudents] = useState<StudentProfile[]>([]);
  const [classes, setClasses] = useState<PortalClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [opError, setOpError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [listClassFilter, setListClassFilter] = useState('');
  const [memberMap, setMemberMap] = useState<Record<string, string[]>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    const [{ data: certData }, { data: sData }, { data: cData }, { data: mData }] = await Promise.all([
      supabase.from('certificates').select('*').order('issued_at', { ascending: false }),
      supabase.from('student_profiles').select('*').order('first_name'),
      supabase.from('portal_classes').select('*').order('title'),
      supabase.from('portal_class_members').select('class_id, student_id').eq('is_active', true),
    ]);
    setCertificates((certData ?? []) as Certificate[]);
    setStudents((sData ?? []) as StudentProfile[]);
    setClasses((cData ?? []) as PortalClass[]);
    const map: Record<string, string[]> = {};
    for (const row of (mData ?? []) as Array<{ class_id: string; student_id: string }>) {
      (map[row.class_id] ??= []).push(row.student_id);
    }
    setMemberMap(map);
    setLoading(false);
  };

  // Schüler des im Formular gewählten Kurses (ohne Kurs: alle)
  const formStudents = useMemo(() => {
    if (!form.class_id) return students;
    const ids = new Set(memberMap[form.class_id] ?? []);
    return students.filter(s => ids.has(s.id));
  }, [students, memberMap, form.class_id]);

  const studentMap = useMemo(
    () => Object.fromEntries(students.map(s => [s.id, studentName(s)])),
    [students]
  );
  const classMap = useMemo(
    () => Object.fromEntries(classes.map(c => [c.id, c.title])),
    [classes]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return certificates.filter(c => {
      if (listClassFilter && c.class_id !== listClassFilter) return false;
      if (!q) return true;
      const name = (studentMap[c.student_id] ?? '').toLowerCase();
      const cls = c.class_id ? (classMap[c.class_id] ?? '').toLowerCase() : '';
      return name.includes(q) || c.title.toLowerCase().includes(q) || cls.includes(q);
    });
  }, [certificates, search, listClassFilter, studentMap, classMap]);

  const openCreate = () => {
    setForm(emptyForm());
    setFile(null);
    setShowForm(true);
  };

  const MAX_CERT_BYTES = 20 * 1024 * 1024;
  const ALLOWED_CERT_TYPES = new Set(['application/pdf', 'image/png', 'image/jpeg']);
  const ALLOWED_CERT_EXTS = new Set(['pdf', 'png', 'jpg', 'jpeg']);

  const uploadFile = async (f: File): Promise<{ path: string; url: string }> => {
    if (f.size > MAX_CERT_BYTES) {
      throw new Error(`Datei zu groß (max. ${MAX_CERT_BYTES / (1024 * 1024)} MB).`);
    }
    const ext = (f.name.split('.').pop() ?? '').toLowerCase();
    if (!ALLOWED_CERT_TYPES.has(f.type) || !ALLOWED_CERT_EXTS.has(ext)) {
      throw new Error('Nur PDF, PNG oder JPG sind erlaubt.');
    }
    const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from('certificates').upload(path, f, {
      contentType: f.type,
      upsert: false,
    });
    if (error) throw error;
    // Bucket is private now — file_url is retained as a legacy label but
    // downloads happen via short-lived signed URLs derived from storage_path.
    const { data } = supabase.storage.from('certificates').getPublicUrl(path);
    return { path, url: data.publicUrl };
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.student_id || !form.title.trim() || !file) return;

    setSaving(true);
    try {
      setUploading(true);
      const { path: filePath, url: fileUrl } = await uploadFile(file);
      setUploading(false);

      const payload = {
        student_id: form.student_id,
        class_id: form.class_id || null,
        title: form.title.trim(),
        file_url: fileUrl,
        storage_path: filePath,
        issued_at: form.issued_at || todayIso(),
      };
      const { data, error } = await supabase
        .from('certificates')
        .insert(payload)
        .select()
        .single();
      if (error) throw error;
      setCertificates(prev => [data as Certificate, ...prev]);
      setShowForm(false);
    } catch (err) {
      if (import.meta.env.DEV) console.error(err);
      const msg = err instanceof Error ? err.message : String(err);
      const hint = /bucket|not found|404/i.test(msg)
        ? ' Hinweis: Der Storage-Bucket "certificates" fehlt vermutlich. Bitte die Migration 20260520000002 in Supabase ausführen.'
        : /policy|permission|row-level|unauthorized|403/i.test(msg)
          ? ' Hinweis: Keine Berechtigung. Prüfe, ob dein Konto Portal-Admin ist.'
          : '';
      setOpError(`Fehler beim Speichern: ${msg}${hint}`);
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    const cert = certificates.find(c => c.id === id);
    if (cert?.storage_path) {
      // Best-effort file cleanup; even if this fails the DB row is removed
      // and the file becomes an orphan without user impact.
      await supabase.storage.from('certificates').remove([cert.storage_path]);
    }
    const { error } = await supabase.from('certificates').delete().eq('id', id);
    if (!error) setCertificates(prev => prev.filter(c => c.id !== id));
    setConfirmDelete(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold tracking-[0.12em] uppercase text-[#970000]/70 mb-1">
            Portal
          </p>
          <h2 className="text-3xl font-bold text-[#1f1f1f]">Zertifikate</h2>
          <p className="text-gray-500 text-sm mt-1">
            Zertifikate als PDF oder Bild für einzelne Schüler hochladen.
          </p>
        </div>
        <button onClick={openCreate} className={primaryBtn}>
          <Plus className="h-4 w-4 mr-1" />
          Hinzufügen
        </button>
      </div>

      {opError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl px-4 py-3 text-sm flex items-center justify-between">
          <span>{opError}</span>
          <button onClick={() => setOpError('')} className="ml-4 text-red-500 hover:text-red-700">✕</button>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[24px] w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-[#eadfd6]">
              <h3 className="text-lg font-bold text-[#1f1f1f]">Neues Zertifikat</h3>
              <button
                onClick={() => setShowForm(false)}
                className="p-2 rounded-xl hover:bg-[#f8f3ef]"
              >
                <X className="h-5 w-5 text-[#6b6b6b]" />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">
                  1. Kurs wählen
                </label>
                <select
                  value={form.class_id}
                  onChange={e => {
                    const classId = e.target.value;
                    setForm(p => {
                      // Gewählten Schüler verwerfen, wenn er nicht im neuen Kurs ist
                      const stillValid =
                        !classId || (memberMap[classId] ?? []).includes(p.student_id);
                      return { ...p, class_id: classId, student_id: stillValid ? p.student_id : '' };
                    });
                  }}
                  className={inputClass}
                >
                  <option value="">Alle Schüler (kein Kurs)</option>
                  {classes.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.title}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-[#9b9490] mt-1">
                  Die Schülerliste zeigt dann nur die Mitglieder dieses Kurses.
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">
                  2. Schüler wählen *
                </label>
                <select
                  value={form.student_id}
                  onChange={e => setForm(p => ({ ...p, student_id: e.target.value }))}
                  className={inputClass}
                  required
                >
                  <option value="">
                    {formStudents.length === 0
                      ? 'Keine Schüler in diesem Kurs'
                      : `Schüler wählen… (${formStudents.length})`}
                  </option>
                  {formStudents.map(s => (
                    <option key={s.id} value={s.id}>
                      {studentName(s)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">Titel *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                  className={inputClass}
                  required
                  placeholder="z.B. Teilnahmezertifikat"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">
                  Ausgestellt am
                </label>
                <input
                  type="date"
                  value={form.issued_at}
                  onChange={e => setForm(p => ({ ...p, issued_at: e.target.value }))}
                  className={inputClass}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">
                  Datei (PDF oder Bild) *
                </label>
                {file ? (
                  <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                    <FileText className="h-4 w-4 text-emerald-700 shrink-0" />
                    <span className="text-sm font-medium text-emerald-700 flex-1 truncate">
                      {file.name}
                    </span>
                    <span className="text-xs text-emerald-600">
                      {(file.size / 1024 / 1024).toFixed(1)} MB
                    </span>
                    <button
                      type="button"
                      onClick={() => setFile(null)}
                      className="text-emerald-700 hover:text-red-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full flex flex-col items-center gap-2 p-6 border-2 border-dashed border-[#ddd6cf] rounded-2xl hover:border-[#970000] hover:bg-[#faf7f5] transition-colors"
                  >
                    <Upload className="h-6 w-6 text-[#9b9490]" />
                    <span className="text-sm font-medium text-[#5f5148]">Datei auswählen</span>
                    <span className="text-xs text-[#9b9490]">PDF, PNG oder JPG · max. 20 MB</span>
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,image/png,image/jpeg,.pdf,.png,.jpg,.jpeg"
                  className="hidden"
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) setFile(f);
                    e.target.value = '';
                  }}
                />
                {!file && (
                  <p className="text-xs text-red-500 mt-1">Bitte eine Datei auswählen.</p>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving} className={primaryBtn}>
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      {uploading ? 'Wird hochgeladen…' : 'Speichert…'}
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-1" />
                      Speichern
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className={secondaryBtn}
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[24px] p-6 w-full max-w-sm">
            <p className="font-semibold text-[#1f1f1f] mb-4">Zertifikat wirklich löschen?</p>
            <div className="flex gap-3">
              <button onClick={() => handleDelete(confirmDelete)} className={dangerBtn}>
                Löschen
              </button>
              <button onClick={() => setConfirmDelete(null)} className={secondaryBtn}>
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={cardClass + ' p-4 grid md:grid-cols-2 gap-3'}>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9b9490]" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Nach Schüler, Titel oder Kurs suchen…"
            className={inputClass + ' pl-10'}
          />
        </div>
        <select
          value={listClassFilter}
          onChange={e => setListClassFilter(e.target.value)}
          className={inputClass}
        >
          <option value="">Alle Kurse</option>
          {classes.map(c => (
            <option key={c.id} value={c.id}>{c.title}</option>
          ))}
        </select>
      </div>

      <div className={cardClass + ' overflow-hidden'}>
        {loading ? (
          <div className="p-10 text-center text-[#6b6b6b]">Wird geladen…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-[#6b6b6b]">
            <Award className="h-10 w-10 text-[#d6cec5] mx-auto mb-3" />
            {certificates.length === 0
              ? 'Noch keine Zertifikate vorhanden.'
              : 'Keine Zertifikate gefunden.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#eadfd6]">
                  <th className="text-left p-4 font-semibold text-[#6b6b6b]">Schüler</th>
                  <th className="text-left p-4 font-semibold text-[#6b6b6b]">Titel</th>
                  <th className="text-left p-4 font-semibold text-[#6b6b6b]">Kurs</th>
                  <th className="text-left p-4 font-semibold text-[#6b6b6b]">Ausgestellt</th>
                  <th className="text-left p-4 font-semibold text-[#6b6b6b]">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.id} className="border-b border-[#eadfd6] hover:bg-[#faf8f6]">
                    <td className="p-4 font-medium text-[#1f1f1f]">
                      {studentMap[c.student_id] ?? 'Unbekannt'}
                    </td>
                    <td className="p-4 text-[#1f1f1f]">{c.title}</td>
                    <td className="p-4 text-[#6b6b6b]">
                      {c.class_id ? classMap[c.class_id] ?? '–' : '–'}
                    </td>
                    <td className="p-4 text-[#6b6b6b]">
                      {new Date(c.issued_at).toLocaleDateString('de-DE')}
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={async () => {
                            const url = await resolveFileUrl('certificates', c.storage_path, c.file_url);
                            if (url) window.open(url, '_blank', 'noopener,noreferrer');
                            else setOpError('Signierte URL konnte nicht erstellt werden.');
                          }}
                          className={secondaryBtn}
                        >
                          <ExternalLink className="h-3.5 w-3.5 mr-1" />
                          Ansehen
                        </button>
                        <button
                          onClick={() => setConfirmDelete(c.id)}
                          className="p-2 rounded-xl text-red-500 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CertificatesManager;
